// eslint-disable-next-line @typescript-eslint/no-var-requires
/**
 * Minimal replacement for styled-components `css` tagged template.
 * @param {TemplateStringsArray} strings
 * @param {...(string|number)} expressions
 * @returns {string}
 */
const css = (strings, ...expressions) =>
	strings.reduce((acc, str, index) => `${acc}${str}${String(expressions[index] ?? '')}`, '')

const COLORS = {
	BLUE: `#568dfe`,
	BLUE_HOVER: `#228bd6`,
	SECOND_BLUE: `#EAF1FF`,
	SECOND_BLUE_HOVER: `#80aeff`,
	BLACK: `#1c1c1c`,
	GRAY: `#d2d2d2`,
	DARK_GRAY: `#4f4f4f`,
	DARK_GRAY_1: `#262a2f`,
	DARK_GRAY_2: `#1d1d1f`,
	WHITE_GRAY: `#fafafa`,
	LIGHT_GRAY: `#f2f2f2`,
	MEDIUM_GRAY: `#96999F`,
	GRAY_2: `#7e7e7e`,
	GRAY_3: `#dfdfdf`,
	GRAY_4: `#eee`,
	GRAY_5: `#e0e0e0`,
	GRAY_6: `#3f404d`,
	GRAY_7: `#f6f6f6`,
	GRAY_8: `#f7f7f7`,
	GRAY_9: `#828282`,
	GRAY_10: `#202020`,
	GRAY_11: `#575757`,
	GRAY_12: `#545563`,
	GRAY_13: `#7c8087`,
	GRAY_14: `#dadada`,
	GRAY_15: `#292424`,
	GRAY_16: `#efeff1`,
	GRAY_17: `#7C8087`,
	GRAY_18: `#e3e2e2`,
	GRAY_19: `#d9d9d9`,
	GRAY_20: `#bfbfbf`,
	RED: `#ff6868`,
	WHITE: `#ffffff`,
	ORANGE: `#FF7733`,
	LOADER: `#D4E2FC`
}

const BREAKPOINTS = {
	screenXxxs: `360px`,
	screenXxs: `375px`,
	screenXs: `480px`,
	screenSm: `576px`,
	screenMd: `768px`,
	screenLg: `992px`,
	screenXl: `1200px`,
	screenXxl: `1400px`,
	screenXxxl: `1600px`,
	screenXxxxl: `1800px`
}

const GRADIENTS = {
	GRADIENT_1: `linear-gradient(127.25deg, #F1F1F1 0%, #F6F6F6 73.74%)`
}

const ORDER_COMPONENT_VARS = {
	order: {
		extendedTabBorderColor: COLORS.LIGHT_GRAY,
		elementsBorderRadius: `8px`
	}
}

const TYPOGRAPHY_CONFIG = {
	h1: css`
		font-size: 32px;
		line-height: 38px;

		@media (min-width: ${BREAKPOINTS.screenLg}) {
			font-size: 28px;
			line-height: 34px;
			margin-bottom: 32px;
		}
	`,
	h2: css`
		font-size: 28px;
		line-height: 34px;

		@media (min-width: ${BREAKPOINTS.screenLg}) {
			font-size: 24px;
			line-height: 30px;
			margin-bottom: 24px;
		}
	`,
	h3: css`
		font-size: 24px;
		line-height: 30px;

		@media (min-width: ${BREAKPOINTS.screenLg}) {
			font-size: 20px;
			line-height: 26px;
			margin-bottom: 24px;
		}
	`,
	h4: css`
		font-size: 20px;
		line-height: 26px;

		@media (min-width: ${BREAKPOINTS.screenLg}) {
			font-size: 17px;
			line-height: 24px;
			margin-bottom: 16px;
		}
	`,
	h5: css`
		font-size: 17px;
		line-height: 24px;
		margin-bottom: 8px;
	`,
	subtitle: css`
		font-weight: 600;
		font-size: 16px;
		line-height: 22px;
	`,
	primary: css`
		font-weight: 500;
		font-size: 16px;
		line-height: 22px;
	`,
	body: css`
		font-weight: 400;
		font-size: 14px;
		line-height: 20px;
	`,
	secondary: css`
		font-weight: 400;
		font-size: 16px;
		line-height: 22px;
	`,
	footnote: css`
		font-weight: 400;
		font-size: 12px;
		line-height: 18px;
	`
}

const defaultTheme = {
	name: `Default`,
	...ORDER_COMPONENT_VARS,
	colors: {
		primaryColor: COLORS.BLUE,
		blueLink: COLORS.BLUE,
		purpleColor: `#7856FE`,
		secondPrimaryColor: COLORS.BLUE,
		secondPrimaryHoverColor: `#D4E2FC`,
		secondaryColor: COLORS.BLACK,
		secondHoverColor: COLORS.SECOND_BLUE_HOVER,
		mutedTextColor: COLORS.GRAY_14,
		linkColor: COLORS.BLUE,
		linkHoverColor: `#2469f5`,
		backgroundQuaternary: COLORS.GRAY,
		foregroundQuaternary: `#848587`,
		backgroundScreen: COLORS.WHITE_GRAY,
		mutedBackground: COLORS.LIGHT_GRAY,
		topPanelBackground: COLORS.LIGHT_GRAY,
		textColorTertiary: `#f5f5f5`,
		borderColor: COLORS.GRAY_3,
		borderColor2: COLORS.GRAY_4,
		borderColor3: COLORS.GRAY_5,
		borderColor4: COLORS.GRAY_14,
		borderColor5: COLORS.GRAY_15,
		borderColor6: COLORS.GRAY_13,
		borderColor7: COLORS.GRAY_19,
		borderColor8: `#DADADA`,
		borderOnDarkColor: COLORS.DARK_GRAY,
		iconTextColor: COLORS.GRAY_6,
		colorIconPrimary: COLORS.MEDIUM_GRAY,
		colorIconSecondary: COLORS.BLUE,
		backgroundTint: COLORS.GRAY_7,
		textBannerColor: COLORS.LIGHT_GRAY,
		footerTextColor: COLORS.GRAY_9,
		footerTitleColor: COLORS.GRAY_5,
		bodyBgColor: COLORS.GRAY_4,
		firstGradient: GRADIENTS.GRADIENT_1,
		textColorSecondary: COLORS.GRAY_12,
		textColor2: COLORS.GRAY_10,
		textColor3: COLORS.GRAY_6,
		textColor4: COLORS.GRAY_9,
		textColor5: COLORS.GRAY_2,
		textColor6: COLORS.GRAY_13,
		textColor7: COLORS.GRAY_15,
		textColor8: COLORS.DARK_GRAY_2,
		textColor9: COLORS.GRAY_17,
		textColor10: COLORS.GRAY_20,
		backgroundColor1: COLORS.GRAY_13,
		backgroundColor2: COLORS.DARK_GRAY_1,
		backgroundColor3: COLORS.GRAY_12,
		backgroundColor4: COLORS.GRAY_16,
		backgroundColor5: COLORS.SECOND_BLUE,
		backgroundColor6: `#F5F5F7`,
		backgroundColor7: `#DADADA`,
		bonusIconColor: COLORS.GRAY_11,
		pageTitleColor: COLORS.GRAY_12,
		elementHoverColor: COLORS.BLUE_HOVER,
		redColor: COLORS.RED,
		whiteColor: COLORS.WHITE,
		warningColor: COLORS.ORANGE,
		catalogItemBg: `#f5f5f5`,
		selectColor: `#D4E2FC`,
		loaderColor: COLORS.LOADER
	},
	typography: {
		...TYPOGRAPHY_CONFIG,
		// base 1rem === 10px
		fontFamily: `"SFProDisplay", sans-serif`,
		fontSizeBase: `1.5rem`,
		textColor: COLORS.GRAY_15,
		textColorSecondary: COLORS.GRAY_2,
		textColorTertiary: COLORS.GRAY_8,
		textColorAccent: COLORS.BLUE,
		// Titles
		typographyTitleFontWeight: `500`,
		headingLarge: `5.6rem`,
		heading1Size: `3.2rem`,
		heading1SizeMobile: `2.8rem`,
		heading2Size: `2.2rem`,
		heading2SizeXS: `1.8rem`,
		heading3Size: `1.6rem`,
		textTitle: `1.7rem`,
		// Promo
		promoTitle: `6.4rem`,
		promoTitleMobile: `5rem`,
		promoSubTitle: `5.6rem`,
		promoSubTitleMobile: `4rem`,
		textFootnote: `1.4rem`,
		textLink: `1.6rem`,
		textOldPrice: `1.6rem`,
		textPrice: `2.2rem`,
		textMedium: `1.6rem`,
		textSmall: `1.4rem`,
		textLarge: `2.4rem`,
		textLargeMobile: `2.1rem`,
		textButton: `1.3rem`,
		textMenu: `1.8rem`,
		mobileTextRegular: `1.3rem`,
		mobileTextTitle: `1.8rem`,
		mobileTextPrice: `2rem`,
		mobileTextOldPrice: `1.3rem`,
		mobileTextLink: `1.3rem`,
		mobileTextLarge: `1.8rem`,
		showAllButtonText: `1.4rem`,
		textNote: `1.5rem`
	},
	gradients: {
		cardColor: `linear-gradient(127.25deg, #F1F1F1 0%, #F6F6F6 73.74%);`,
		DarkBackgroundCard: `linear-gradient(109.46deg, #F2F2F2 0%, #ECECEC 100%)`,
		gradient1: `linear-gradient(127.25deg, #F1F1F1 0%, #F6F6F6 73.74%);`
	},
	borderRadius: {
		default: `3.2rem`,
		small: `1.2rem`,
		medium: `1.8rem`,
		borderRadiusBig: `.8rem`
	},
	shadow: {
		boxShadowBase: `0px 18px 32px rgba(41, 36, 36, 0.25)`
	},
	breakpoints: BREAKPOINTS,
	animations: {
		animationDurationVSlow: `500ms`,
		animationDurationSlow: `300ms`,
		animationDurationBase: `200ms`,
		animationDurationFast: `100ms`,
		easeBase: `cubic-bezier(0.250, 0.460, 0.450, 0.940)`
	},
	base: {
		disabledColor: COLORS.GRAY
	},
	mainLayout: {
		headerHeight: `60px`,
		asideWidth: `80px`,
		mainPadding: `2rem`,
		maxContainerWidth: `2034px`,
		maxContainerInnerWidth: `1440px`,
		modalsMaxSize: `101.5rem`,
		topBannerHeight: `40px`,
		topPanelHeight: `30px`
	},
	mainLayoutXs: {
		headerHeight: `56px`,
		asideWidth: `56px`,
		mainPadding: `1.6rem`
	},
	aside: {
		asideColor: `#eee`
	},
	header: {
		// f2 means HEX 0.95 opacity
		// headerColor: COLORS.BLACK + `f2`,
		headerColor: COLORS.GRAY_15
	},
	footer: {
		footerColor: COLORS.GRAY_15
	},
	switch: {
		switchHeight: `30px`,
		switchMinWidth: `56px`,
		switchPinSize: `24px`,
		switchPadding: `3px`
	},
	scrollbar: {
		width: `1.4rem`,
		backgroundColor: COLORS.WHITE_GRAY,
		color: COLORS.BLACK,
		hover: COLORS.BLUE
	},
	personal: {
		defaultBorderRadius: `8px`,
		formItemRadius: `20px`,
		sectionBorderRadius: `12px`,
		formItemHeight: `40px`,
		formItemBorderColor: COLORS.GRAY_3,
		mainTextColor: COLORS.DARK_GRAY_2,
		secondTextColor: COLORS.GRAY_2,
		itemBackgroundColor: `#fff`
	},
	mixins: {
		paragraphReset: `
  margin: 0;
  padding: 0;
  `,
		buttonReset: css`
			cursor: pointer;
			border: none;
			margin: 0;
			padding: 0;
			width: auto;
			overflow: visible;
			background: transparent;
			color: inherit;
			font: inherit;
			line-height: normal;
			-webkit-font-smoothing: inherit;
			-moz-osx-font-smoothing: inherit;
			-webkit-appearance: none;

			&::-moz-focus-inner {
				border: 0;
				padding: 0;
			}
		`,
		visuallyHidden: css`
			position: absolute;
			width: 1px;
			height: 1px;
			margin: -1px;
			border: 0;
			padding: 0;
			white-space: nowrap;
			clip-path: inset(100%);
			clip: rect(0 0 0 0);
			overflow: hidden;
		`,

		listReset: css`
			margin: 0;
			padding: 0;
			list-style: none;
		`,

		hideScrollbar: css`
			-ms-overflow-style: none;
			scrollbar-width: none;

			&::-webkit-scrollbar {
				width: 0;
				height: 0;
				background: transparent;
			}
		`,
		productSetList: css`
			flex-wrap: wrap;
			height: auto;

			& > * {
				flex-basis: 33%;
				width: 33%;

				&:last-child {
					flex-basis: 99%;
					width: 99%;
				}
			}
		`,
		orderedListReset: css`
			list-style: none;
			padding: 0;
			margin: 0;
		`,
		cardHoverEffect: css`
			z-index: 1;
			overflow: hidden;
			position: relative;

			font-size: 1rem;

			& > * {
				z-index: 3;
			}

			&:before {
				content: '';
				position: absolute;
				top: 0;
				right: 0;
				transform: translate(50%, -50%);
				opacity: 0;
				background-color: ${COLORS.WHITE_GRAY};
				width: 0;
				height: 0;
				transition: all 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
				border-radius: 50%;
				z-index: 2;
			}

			&:hover {
				&:before {
					width: 300%;
					height: 300%;
					opacity: 1;
				}
			}
		`
	}
}

module.exports = defaultTheme
